<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import BaseSticker from "@/components/ui/BaseSticker/BaseSticker.vue";
import MenuList from "@/components/ui/MenuList/MenuList.vue";
import MenuListHeader from "@/components/ui/MenuList/MenuListHeader.vue";
import MenuListItem from "@/components/ui/MenuList/MenuListItem.vue";
import MenuListItems from "@/components/ui/MenuList/MenuListItems.vue";
import MenuListSearch from "@/components/ui/MenuList/MenuListSearch.vue";
import MenuListTitle from "@/components/ui/MenuList/MenuListTitle.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import type { Sticker } from "@/model/StickerType";
import { stickersEqual } from "@/model/StickerType";
import { useStickerTypeStore } from "@/store/stickerType";

const props = withDefaults(
  defineProps<{
    selected: Sticker | Array<Sticker | null> | null;
    multiselect?: boolean;
  }>(),
  { multiselect: false },
);

const emit = defineEmits<{ select: [Sticker | null] }>();

const { t } = useI18n();

const searchQuery = ref("");

const stickerTypes = computed(() => useStickerTypeStore().stickerTypes);

const filteredStickers = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  return q
    ? stickerTypes.value.filter((s) => s.label.toLowerCase().includes(q))
    : stickerTypes.value;
});

// Normalize `selected` into an array so `isSelected` can use the same logic
// for both single- and multi-select callers. A `null` selection means "no
// sticker", which is itself a valid selection — the "No sticker" entry shows
// as selected when present.
const selectedList = computed(() =>
  Array.isArray(props.selected) ? props.selected : [props.selected],
);

function isSelected(sticker: Sticker | null): boolean {
  return selectedList.value.some((s) => stickersEqual(s, sticker));
}

function select(sticker: Sticker | null) {
  // Single-select: re-clicking the selected sticker clears it.
  if (!props.multiselect && sticker && isSelected(sticker)) {
    emit("select", null);
    return;
  }
  emit("select", sticker);
}

const noStickerVisible = computed(() =>
  t("stickerPicker.noSticker").toLowerCase().includes(searchQuery.value.trim().toLowerCase()),
);
</script>

<template>
  <MenuList :selectable="true" :multiselect>
    <MenuListHeader>
      <MenuListTitle>{{ $t("stickerPicker.title") }}</MenuListTitle>
      <MenuListSearch
        :placeholder="$t('stickerPicker.search')"
        :results-count="filteredStickers.length + (noStickerVisible ? 1 : 0)"
        @search="searchQuery = $event"
      />
    </MenuListHeader>
    <MenuListItems>
      <MenuListItem v-if="noStickerVisible" :selected="isSelected(null)" @click="select(null)">
        <template #before>
          <SvgIcon name="general/sticker" width="20" height="20" />
        </template>
        {{ $t("stickerPicker.noSticker") }}
      </MenuListItem>
      <MenuListItem
        v-for="sticker in filteredStickers"
        :key="`${sticker.icon}-${sticker.color}`"
        :selected="isSelected(sticker)"
        @click="select({ icon: sticker.icon, color: sticker.color })"
      >
        <template #before>
          <BaseSticker :sticker class="sticker-icon" />
        </template>
        {{ sticker.label }}
      </MenuListItem>
      <MenuListItem v-if="!filteredStickers.length && !noStickerVisible" static>
        {{ $t("searchResults.noResults") }}
      </MenuListItem>
    </MenuListItems>
  </MenuList>
</template>

<style lang="scss" scoped>
.sticker-icon {
  width: 20px;
  height: 20px;
  padding: 0;
}
</style>
